import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { api } from "@/lib/api";
import * as Icons from "lucide-react";
import { ArrowRight, Ambulance, HeartPulse, Star, ShieldCheck, Sparkles, Stethoscope, Phone, MessageCircle } from "lucide-react";
import { motion, FadeUp, HeroFade, StaggerGrid, StaggerItem } from "@/lib/motion";
import { DynamicSlot } from "@/components/home/HomeSectionRenderer";
import { Hero } from "@/components/home/Hero";
import { useSEO } from "@/hooks/useSEO";

const IconOf = ({ name, className }) => {
  const Ico = Icons[name] || Stethoscope;
  return <Ico className={className} strokeWidth={1.75} />;
};

export default function Home() {
  const { site } = useOutletContext();
  const [departments, setDepartments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [homeSections, setHomeSections] = useState([]);

  useEffect(() => {
    api.get("/departments").then(({ data }) => setDepartments(data));
    api.get("/doctors").then(({ data }) => setDoctors(data));
    api.get("/services").then(({ data }) => setServices(data));
    api.get("/testimonials").then(({ data }) => setTestimonials(data));
    api.get("/facilities").then(({ data }) => setFacilities(data));
    api.get("/home-sections").then(({ data }) => setHomeSections(data)).catch(() => {});
  }, []);

  const whyChooseUs = site?.why_choose_us || [];
  const whatsapp = site?.whatsapp || "923419300560";
  const phone = site?.phone || "0341-9300560";

  useSEO({
    title: "Best 24/7 Hospital & Healthcare",
    description: "Afzal Medical Complex offers 24/7 emergency care, board-certified specialists, digital pathology, radiology (CT/MRI), ICUs, and comprehensive diagnostics with patient-first warmth.",
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "MedicalOrganization",
          "@id": "https://afzalmedicalcomplex.com/#organization",
          "name": "Afzal Medical Complex",
          "url": "https://afzalmedicalcomplex.com",
          "logo": "https://afzalmedicalcomplex.com/favicon-180.png",
          "telephone": "+923419300560",
          "email": "contact@afzalmedicalcomplex.com",
          "address": {
            "@type": "PostalAddress",
            "addressCountry": "PK",
            "streetAddress": "Main GT Road"
          }
        },
        {
          "@type": "FAQPage",
          "@id": "https://afzalmedicalcomplex.com/#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What are the timings of Afzal Medical Complex?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Afzal Medical Complex is open 24 hours a day, 7 days a week, providing round-the-clock emergency, pharmacy, pathology, and ambulance services."
              }
            },
            {
              "@type": "Question",
              "name": "How can I book an appointment with a doctor?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "You can book an appointment by calling 0341-9300560, visiting the Contact page, or clicking the direct WhatsApp link on the site to schedule with a consultant."
              }
            }
          ]
        }
      ]
    }
  });

  return (
    <div data-testid="home-page">
      <Hero site={site} doctors={doctors} />

      {/* ==================== CEO MESSAGE ==================== */}
      <section className="section bg-white" data-testid="ceo-message-section">
        <div className="container-medi">
          <FadeUp className="flex flex-col md:flex-row items-center gap-8 bg-slate-50 rounded-3xl p-8 md:p-12 shadow-sm border border-slate-100">
            <div className="flex-shrink-0">
              <img src="/ceo.png" alt="CEO" className="w-[200px] h-[200px] object-cover rounded-full border-4 border-white shadow-medi-md mx-auto" />
            </div>
            <div className="text-center md:text-left space-y-4">
              <div className="overline text-red-700">Message from the CEO</div>
              <h2 className="text-2xl md:text-3xl font-heading font-semibold text-slate-900">Compassionate care at the lowest prices, driven by advanced technology.</h2>
              <p className="text-slate-600 leading-relaxed">
                As a dedicated trust hospital, our primary mission is to serve the community without compromising on quality. 
                We believe that every patient deserves access to state-of-the-art medical equipment and top-tier expertise, 
                which is why we continuously invest in advanced technology while keeping our services affordable and accessible to everyone.
              </p>
              <div className="font-semibold text-slate-900 mt-4">
                Managing Director / CEO <br/>
                <span className="text-sm font-normal text-slate-500">Afzal Medical Complex</span>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>

      {/* ==================== WHY CHOOSE US ==================== */}
      <DynamicSlot position="after_hero" sections={homeSections} />
      <section className="section" data-testid="why-choose-section">
        <div className="container-medi">
          <FadeUp className="max-w-2xl mb-12">
            <div className="overline mb-3">Why Choose Us</div>
            <h2 className="text-3xl lg:text-4xl font-heading font-semibold text-slate-900">A trusted name in healthcare — for good reason.</h2>
            <p className="mt-4 text-slate-600 text-lg">Quality care, modern facilities, and a compassionate team — at prices you can plan around.</p>
          </FadeUp>
          <StaggerGrid className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((w, i) => (
              <StaggerItem key={w.title} index={i} className="card-medi p-8">
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-50 to-red-100/50 text-red-700 grid place-items-center mb-5 ring-1 ring-red-100">
                  <IconOf name={w.icon} className="w-6 h-6" />
                </div>
                <h3 className="font-heading font-semibold text-lg text-slate-900">{w.title}</h3>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{w.description}</p>
              </StaggerItem>
            ))}
          </StaggerGrid>
        </div>
      </section>

      {/* ==================== SERVICES ==================== */}
      <DynamicSlot position="after_why" sections={homeSections} />
      <section className="section bg-slate-50/60" data-testid="services-section">
        <div className="container-medi">
          <FadeUp className="max-w-2xl mb-10">
            <div className="overline mb-3">Our Services</div>
            <h2 className="text-3xl lg:text-4xl font-heading font-semibold text-slate-900">Comprehensive care under one roof</h2>
            <p className="mt-4 text-slate-600">From routine check-ups to complex surgeries, our services are designed around your comfort and safety.</p>
          </FadeUp>
          <StaggerGrid className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.slice(0, 8).map((s, i) => (
              <StaggerItem key={s.id} index={i} className="card-medi p-5" data-testid={`service-card-${s.id}`}>
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-700 grid place-items-center mb-3">
                  <IconOf name={s.icon} className="w-5 h-5" />
                </div>
                <div className="font-heading font-medium text-slate-900">{s.name}</div>
                <div className="text-xs text-slate-500 mt-1.5 line-clamp-2">{s.description}</div>
              </StaggerItem>
            ))}
          </StaggerGrid>
          <FadeUp className="mt-8 text-center" delay={0.1}>
            <Link to="/services" className="btn-outline" data-testid="services-see-all">See all services <ArrowRight className="w-4 h-4" /></Link>
          </FadeUp>
        </div>
      </section>

      {/* ==================== FACILITIES ==================== */}
      <DynamicSlot position="after_services" sections={homeSections} />
      <section className="section" data-testid="facilities-section">
        <div className="container-medi">
          <FadeUp className="max-w-2xl mb-12">
            <div className="overline mb-3">Our Facilities</div>
            <h2 className="text-3xl lg:text-4xl font-heading font-semibold text-slate-900">Equipped for every step of your care journey</h2>
            <p className="mt-4 text-slate-600 text-lg">Modern infrastructure, sterilised environments and the latest equipment — because quality care starts with quality facilities.</p>
          </FadeUp>
          <StaggerGrid className="grid md:grid-cols-2 gap-6">
            {facilities.map((f, i) => (
              <StaggerItem key={f.id} index={i} className="card-medi group overflow-hidden">
                <div className="grid grid-cols-5 h-full">
                  <div className="col-span-2 relative overflow-hidden bg-slate-100">
                    {f.image_url && <img src={f.image_url} alt={f.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />}
                  </div>
                  <div className="col-span-3 p-6">
                    <div className="w-10 h-10 rounded-xl bg-red-50 text-red-700 grid place-items-center mb-3">
                      <IconOf name={f.icon} className="w-5 h-5" />
                    </div>
                    <h3 className="font-heading font-semibold text-lg text-slate-900">{f.name}</h3>
                    <p className="mt-1.5 text-sm text-slate-600 leading-relaxed line-clamp-2">{f.description}</p>
                    <ul className="mt-3 space-y-1.5">
                      {(f.highlights || []).slice(0, 3).map((h) => (
                        <li key={h} className="flex items-center gap-2 text-xs text-slate-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" /> {h}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGrid>
        </div>
      </section>

      {/* ==================== DEPARTMENTS ==================== */}
      <DynamicSlot position="after_facilities" sections={homeSections} />
      <section className="section bg-slate-50/60" data-testid="departments-section">
        <div className="container-medi">
          <FadeUp className="flex flex-wrap items-end justify-between mb-10 gap-4">
            <div className="max-w-2xl">
              <div className="overline mb-3">Departments</div>
              <h2 className="text-3xl lg:text-4xl font-heading font-semibold text-slate-900">Specialised departments, seamless care</h2>
            </div>
            <Link to="/departments" className="text-sm text-red-700 font-medium hover:underline" data-testid="dept-see-all">View all →</Link>
          </FadeUp>
          <StaggerGrid className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {departments.slice(0, 6).map((d, i) => (
              <StaggerItem key={d.id} index={i}>
                <Link to={`/departments/${d.slug || d.id}`} className="card-medi group block" data-testid={`dept-card-${d.id}`}>
                  <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                    {d.image_url && <img src={d.image_url} alt={d.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />}
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-red-50 text-red-700 grid place-items-center">
                        <IconOf name={d.icon} className="w-4 h-4" />
                      </div>
                      <h3 className="font-heading font-semibold text-lg text-slate-900">{d.name}</h3>
                    </div>
                    <p className="text-sm text-slate-600 leading-relaxed line-clamp-2">{d.short_description}</p>
                    <div className="mt-4 text-sm text-red-700 font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">Learn more <ArrowRight className="w-4 h-4" /></div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerGrid>
        </div>
      </section>

      {/* ==================== DOCTORS ==================== */}
      <DynamicSlot position="after_departments" sections={homeSections} />
      <section className="section" data-testid="doctors-section">
        <div className="container-medi">
          <FadeUp className="flex flex-wrap items-end justify-between mb-10 gap-4">
            <div className="max-w-2xl">
              <div className="overline mb-3">Meet Our Team</div>
              <h2 className="text-3xl lg:text-4xl font-heading font-semibold text-slate-900">Doctors who genuinely care</h2>
            </div>
            <Link to="/doctors" className="text-sm text-red-700 font-medium hover:underline" data-testid="doctors-see-all">View all doctors →</Link>
          </FadeUp>
          <StaggerGrid className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {doctors.map((doc, i) => (
              <StaggerItem key={doc.id} index={i}>
                <Link to={`/doctors/${doc.id}`} className="card-medi group block" data-testid={`doctor-card-${doc.id}`}>
                  <div className="aspect-square overflow-hidden bg-slate-100">
                    {doc.photo_url && <img src={doc.photo_url} alt={doc.name} className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />}
                  </div>
                  <div className="p-5">
                    <div className="font-heading font-semibold text-slate-900">{doc.name}</div>
                    <div className="text-sm text-red-700 mt-0.5">{doc.specialty}</div>
                    <div className="text-xs text-slate-500 mt-2 line-clamp-1">{doc.qualifications}</div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerGrid>
        </div>
      </section>

      {/* ==================== TESTIMONIALS ==================== */}
      <DynamicSlot position="after_doctors" sections={homeSections} />
      <section className="section bg-gradient-to-b from-red-50/50 via-white to-white" data-testid="testimonials-section">
        <div className="container-medi">
          <FadeUp className="max-w-2xl mb-12 text-center mx-auto">
            <div className="overline mb-3">Patient Stories</div>
            <h2 className="text-3xl lg:text-4xl font-heading font-semibold text-slate-900">What our patients say</h2>
            <p className="mt-4 text-slate-600 text-lg">Real stories from the families who trust us with their care.</p>
          </FadeUp>
          <StaggerGrid className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {testimonials.map((t, i) => (
              <StaggerItem key={t.id} index={i} className="card-medi p-7 flex flex-col" data-testid={`testimonial-${t.id}`}>
                <div className="flex items-center gap-1 text-amber-500 mb-4">
                  {[...Array(t.rating || 5)].map((_, k) => <Star key={k} className="w-4 h-4 fill-current" strokeWidth={0} />)}
                </div>
                <p className="text-sm text-slate-700 leading-relaxed flex-1 italic">&ldquo;{t.quote}&rdquo;</p>
                <div className="mt-5 pt-5 border-t border-slate-100 flex items-center gap-3">
                  {t.photo_url ? (
                    <img src={t.photo_url} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-red-50 text-red-700 grid place-items-center font-semibold">{t.name?.[0]}</div>
                  )}
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{t.name}</div>
                    <div className="text-xs text-slate-500">{t.condition}</div>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </StaggerGrid>
        </div>
      </section>

      {/* ==================== EMERGENCY CTA ==================== */}
      <DynamicSlot position="after_testimonials" sections={homeSections} />
      <section className="section" data-testid="cta-section">
        <div className="container-medi">
          <FadeUp>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-red-700 via-red-700 to-red-900 text-white p-10 lg:p-16">
              <div className="relative z-10 grid lg:grid-cols-5 gap-8 items-center">
                <div className="lg:col-span-3">
                  <div className="inline-flex items-center gap-2 text-red-100 text-sm mb-3">
                    <Ambulance className="w-4 h-4" /> Need urgent care?
                  </div>
                  <h3 className="text-3xl lg:text-4xl font-heading font-semibold leading-tight">Our emergency department is open 24/7 — call or WhatsApp us anytime.</h3>
                  <p className="mt-4 text-red-100">A dedicated team is always ready. Reach out and we&apos;ll respond immediately.</p>
                </div>
                <div className="lg:col-span-2 flex flex-col gap-3">
                  <a href={`tel:${phone}`} className="inline-flex items-center justify-between bg-white text-red-700 hover:bg-red-50 font-semibold rounded-2xl px-5 py-4 transition shadow-lg group" data-testid="cta-call">
                    <span className="inline-flex items-center gap-3"><Phone className="w-5 h-5" /> Call {phone}</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                  </a>
                  <a href={`https://wa.me/${whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-between bg-emerald-500 hover:bg-emerald-600 text-white font-semibold rounded-2xl px-5 py-4 transition shadow-lg group" data-testid="cta-whatsapp">
                    <span className="inline-flex items-center gap-3"><MessageCircle className="w-5 h-5" /> WhatsApp Us</span>
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition" />
                  </a>
                  <Link to="/contact" className="inline-flex items-center justify-between border-2 border-white/50 text-white hover:bg-white/10 font-medium rounded-2xl px-5 py-4 transition" data-testid="cta-contact">
                    <span className="inline-flex items-center gap-3"><Sparkles className="w-5 h-5" /> Contact Us</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              <motion.div
                className="absolute -right-16 -bottom-16 w-80 h-80 bg-red-600 rounded-full opacity-40"
                animate={{ scale: [1, 1.08, 1] }}
                transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
              />
              <motion.div
                className="absolute -right-32 -top-24 w-96 h-96 bg-red-500/40 rounded-full blur-2xl"
                animate={{ scale: [1, 1.12, 1], opacity: [0.35, 0.55, 0.35] }}
                transition={{ repeat: Infinity, duration: 7, ease: "easeInOut" }}
              />
            </div>
          </FadeUp>
        </div>
      </section>

      <DynamicSlot position="after_cta" sections={homeSections} />
    </div>
  );
}
